const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    ),
);

const updateListeners = new Set();

function notifyUpdateListeners(registration) {
  updateListeners.forEach((listener) => {
    listener(registration);
  });
}

export function subscribeToServiceWorkerUpdate(listener) {
  updateListeners.add(listener);
  return () => {
    updateListeners.delete(listener);
  };
}

export async function clearAppCaches() {
  if (!('caches' in window)) {
    return;
  }

  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
}

export async function refreshForAppUpdate(registration) {
  let activeRegistration = registration;

  if (navigator.serviceWorker?.getRegistration) {
    activeRegistration =
      activeRegistration ?? (await navigator.serviceWorker.getRegistration());
  }

  if (activeRegistration?.update) {
    try {
      await activeRegistration.update();
    } catch {
      // Continue with reload/unregister even if update check fails.
    }
  }

  if (activeRegistration?.waiting) {
    return applyWaitingServiceWorkerUpdate(activeRegistration);
  }

  if (activeRegistration) {
    await clearAppCaches();
    await activeRegistration.unregister();
  }

  window.location.reload();
  return true;
}

export function applyWaitingServiceWorkerUpdate(registration) {
  if (!registration?.waiting) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    let reloaded = false;

    const onControllerChange = () => {
      if (reloaded) {
        return;
      }
      reloaded = true;
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
      window.location.reload();
      resolve(true);
    };

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange,
    );
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  });
}

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch(() => {});
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              config?.onUpdate?.(registration);
              notifyUpdateListeners(registration);
            } else {
              config?.onSuccess?.(registration);
            }
          }
        };
      };
    })
    .catch(() => {});
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {});
}
